/**
 * Background Sync API 유틸리티
 * 메인 스레드에서 실행되는 코드
 */

import { createLogger } from "@ict/shared";

const logger = createLogger({ module: "BackgroundSync" });

const SYNC_TAG = "sync-pending-items";

/**
 * Background Sync 지원 여부 확인
 */
export function isBackgroundSyncSupported(): boolean {
  return "serviceWorker" in navigator && "SyncManager" in window;
}

/**
 * Background Sync 등록
 * 오프라인 → 온라인 전환 시 자동으로 sync 이벤트 발생
 */
export async function registerBackgroundSync(): Promise<boolean> {
  if (!isBackgroundSyncSupported()) {
    logger.info("미지원 브라우저");
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    await registration.sync.register(SYNC_TAG);
    logger.info("등록 완료");
    return true;
  } catch (error) {
    logger.error("등록 실패:", error);
    return false;
  }
}

/**
 * Background Sync 메시지 리스너 등록
 * SW에서 BACKGROUND_SYNC_TRIGGER 메시지를 받으면 콜백 실행
 */
export function onBackgroundSyncTrigger(callback: (itemCount: number) => void): () => void {
  const handler = (event: MessageEvent) => {
    if (event.data?.type === "BACKGROUND_SYNC_TRIGGER") {
      const { itemCount } = event.data.payload;
      callback(itemCount);
    }
  };

  navigator.serviceWorker?.addEventListener("message", handler);

  // cleanup 함수 반환
  return () => {
    navigator.serviceWorker?.removeEventListener("message", handler);
  };
}

/**
 * 등록된 Sync 태그 목록 조회
 */
export async function getRegisteredSyncTags(): Promise<string[]> {
  if (!isBackgroundSyncSupported()) {
    return [];
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.sync.getTags();
  } catch (error) {
    logger.error("태그 조회 실패:", error);
    return [];
  }
}

// SyncManager 타입 정의
declare global {
  interface ServiceWorkerRegistration {
    sync: SyncManager;
  }
  interface SyncManager {
    register(tag: string): Promise<void>;
    getTags(): Promise<string[]>;
  }
}
